/**
 * Chain palette + detection — shared between `DispensaryCover.jsx` (SVG art)
 * and `DispensaryDetail.jsx` (header chrome) so the storefront cover and the
 * page header read as one tonal system per chain.
 *
 * IMPORTANT (legal): we never render a brand's logo / wordmark / trademark.
 * We only pull *tonal* colors from each chain's public visual identity to
 * make each shop's page feel native to the shop you're standing in, without
 * implying an official partnership. The dispensary's actual name (which is
 * already public, factual, and unavoidable since we're listing them) is
 * still the only brand-identifying text on the page.
 */

export const CHAIN_PALETTES = {
  sunnyside: {
    bg1: "#1a2b1f",
    bg2: "#3d6b3a",
    accent: "#f0b938",
    leaf: "#8fc555",
    ink: "#f5f1e3",
  },
  trulieve: {
    bg1: "#0e2a26",
    bg2: "#1f5749",
    accent: "#c4a373",
    leaf: "#5dc4a4",
    ink: "#ecf5ef",
  },
  curaleaf: {
    bg1: "#102a1a",
    bg2: "#234f30",
    accent: "#d8b25a",
    leaf: "#6dc77b",
    ink: "#eef5e8",
  },
  verilife: {
    bg1: "#0a2530",
    bg2: "#1a4a5e",
    accent: "#d99b5e",
    leaf: "#7dc4d5",
    ink: "#e7f1f5",
  },
  rise: {
    bg1: "#2d1e3c",
    bg2: "#4a3266",
    accent: "#e8c987",
    leaf: "#b48fd6",
    ink: "#f3ecf8",
  },
  apothecarium: {
    bg1: "#1c1c20",
    bg2: "#363641",
    accent: "#d4a268",
    leaf: "#9fc18a",
    ink: "#f1e7d5",
  },
  beyond: {
    bg1: "#1a1a2e",
    bg2: "#2d2a52",
    accent: "#e8c266",
    leaf: "#85d4a8",
    ink: "#ede9d8",
  },
  hello: {
    bg1: "#1a1a2e",
    bg2: "#2d2a52",
    accent: "#e8c266",
    leaf: "#85d4a8",
    ink: "#ede9d8",
  },
  keystone: {
    bg1: "#1d2c1d",
    bg2: "#3a5a3a",
    accent: "#d4a268",
    leaf: "#9fc18a",
    ink: "#f1ecda",
  },
  restore: {
    bg1: "#102628",
    bg2: "#1f4d52",
    accent: "#d4a268",
    leaf: "#7dc4c4",
    ink: "#e8f0f0",
  },
  solevo: {
    bg1: "#1b1f3a",
    bg2: "#2d3a5c",
    accent: "#d4a268",
    leaf: "#7da8d6",
    ink: "#e9eef5",
  },
  default: {
    bg1: "#0b2a1f",
    bg2: "#1a4d36",
    accent: "#d4a268",
    leaf: "#92d5b8",
    ink: "#f7f3e9",
  },
};

// Detection order: longer / more specific keys first so "sunnyside" beats
// "sunny", etc. (Currently the keys don't conflict but we keep the explicit
// order in case a chain like "sunny *side*" lands next to "sun *grown*".)
const CHAIN_ORDER = [
  "sunnyside",
  "trulieve",
  "curaleaf",
  "verilife",
  "apothecarium",
  "beyond",
  "hello",
  "keystone",
  "restore",
  "solevo",
  "rise",
];

export function detectChain(name = "", slug = "", posHint = "") {
  const haystack = `${name} ${slug} ${posHint}`.toLowerCase();
  for (const k of CHAIN_ORDER) {
    if (haystack.includes(k)) return k;
  }
  return "default";
}

export function paletteFor({ name, slug, posHint } = {}) {
  const chain = detectChain(name, slug, posHint);
  return { chain, ...(CHAIN_PALETTES[chain] || CHAIN_PALETTES.default) };
}
